import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Cart helpers for configured products. A configured product is a box line with its
 * fillings, personal touch and delivery nested under it (parent_id), each child tagged with
 * a hidden `_role` property. Loaded on every page, this module also guards those lines:
 * fillings and personal touches always match their box's quantity and delivery stays at
 * one, whatever changes the cart. The cart note carries a readable copy of every
 * configuration for the team, below a marker so any note the customer wrote is kept.
 * It also marks the page busy while any cart change is in flight, for the cart's loading state.
 */

export const NOTE_MARKER = '— Order details —';

const GUARD_SOURCE = 'uradora-cart-guard';

const ROLE_LABELS = {
  contents: 'Contents',
  personalise: 'Personal touch',
  delivery: 'Delivery',
};

/** @returns {Promise<any>} The AJAX cart */
export async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

/**
 * One block of text per configured product in the cart.
 * @param {any} cart - The AJAX cart
 */
export function buildOrderDetails(cart) {
  const children = new Map();
  for (const item of cart.items ?? []) {
    const parentKey = item.parent_relationship?.parent_key;
    if (!parentKey) continue;
    if (!children.has(parentKey)) children.set(parentKey, []);
    children.get(parentKey).push(item);
  }

  const blocks = [];
  for (const item of cart.items ?? []) {
    const nested = children.get(item.key);
    if (item.parent_relationship || !nested) continue;

    const variant = item.product_has_only_default_variant ? '' : ` (${item.variant_title})`;
    const lines = [`${item.product_title}${variant} × ${item.quantity}`];

    for (const [role, label] of Object.entries(ROLE_LABELS)) {
      for (const child of nested.filter((candidate) => candidate.properties?._role === role)) {
        lines.push(`${label}: ${child.product_title}`);
      }
    }

    for (const [key, value] of Object.entries(item.properties ?? {})) {
      if (!key.startsWith('_') && value) lines.push(`${key}: ${value}`);
    }

    blocks.push(lines.join('\n'));
  }

  return blocks.join('\n\n');
}

/**
 * Replaces the order details in a note, keeping whatever the customer wrote above them.
 * @param {string | null} note
 * @param {string} details
 */
export function mergeNote(note, details) {
  const own = (note ?? '').split(NOTE_MARKER)[0].trimEnd();
  if (!details) return own;
  return own ? `${own}\n\n${NOTE_MARKER}\n${details}` : `${NOTE_MARKER}\n${details}`;
}

/**
 * Rewrites the cart note so its order details match the cart.
 * @param {any} [cart] - The AJAX cart, fetched when not given
 */
export async function syncCartNote(cart) {
  const current = cart ?? (await fetchCart());
  const note = mergeNote(current.note, buildOrderDetails(current));
  if (note === (current.note ?? '')) return current;

  const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ note }),
  });
  return response.json();
}

/**
 * The quantity each out-of-step nested line should have, keyed by line key: its box's
 * quantity for fillings and personal touches, one for delivery.
 * @param {any} cart - The AJAX cart
 * @returns {Record<string, number>}
 */
export function quantityFixes(cart) {
  const lines = new Map((cart.items ?? []).map((item) => [item.key, item]));
  /** @type {Record<string, number>} */
  const fixes = {};

  for (const item of cart.items ?? []) {
    const role = item.properties?._role;
    const parent = lines.get(item.parent_relationship?.parent_key);
    if (!role || !parent) continue;

    const wanted = role === 'delivery' ? 1 : parent.quantity;
    if (item.quantity !== wanted) fixes[item.key] = wanted;
  }

  return fixes;
}

/**
 * When a box can't be matched (a filling has less stock than the box quantity), the box and
 * its per-box lines drop back to the most the filling allows.
 * @param {any} cart - The AJAX cart
 * @returns {Record<string, number>}
 */
function fallbackFixes(cart) {
  const lines = new Map((cart.items ?? []).map((item) => [item.key, item]));
  /** @type {Map<string, any[]>} */
  const perBox = new Map();

  for (const item of cart.items ?? []) {
    const role = item.properties?._role;
    const parentKey = item.parent_relationship?.parent_key;
    if (!parentKey || (role !== 'contents' && role !== 'personalise')) continue;
    if (!perBox.has(parentKey)) perBox.set(parentKey, []);
    perBox.get(parentKey).push(item);
  }

  /** @type {Record<string, number>} */
  const fixes = {};
  for (const [parentKey, children] of perBox) {
    const parent = lines.get(parentKey);
    const most = Math.min(...children.map((child) => child.quantity));
    if (!parent || parent.quantity === most) continue;

    fixes[parentKey] = most;
    for (const child of children) if (child.quantity !== most) fixes[child.key] = most;
  }

  return fixes;
}

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);
}

/**
 * Sets line quantities in one request and re-renders the cart drawer and cart page through
 * the same event Horizon uses for its own quantity changes.
 * @param {Record<string, number>} updates - Quantities keyed by line key
 * @param {object} options
 * @param {string} options.source - Names the caller in the event's detail
 * @param {EventTarget} [options.target] - Dispatches the event from here; defaults to the document
 * @returns {Promise<any>} The cart after the update, or the error response
 */
export async function updateLines(updates, { source, target = document }) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const removing = Object.values(updates).every((quantity) => quantity === 0);

  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: removing ? 'remove' : 'update',
      context: 'cart',
      lines: Object.entries(updates).map(([id, quantity]) => ({ id, quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates,
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });
    const result = await response.json();

    if (result.status) {
      deferred.reject(new Error(result.description || result.message || 'Cart update failed'));
      return result;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
      detail: { source, sections: result.sections, items: result.items, didError: false },
    });
    return result;
  } catch (error) {
    deferred.reject(error);
    return { status: 'error', message: error instanceof Error ? error.message : String(error) };
  }
}

/** @type {Promise<void> | null} */
let running = null;
let queued = false;

/** True while a guard pass is still checking or fixing quantities, before it updates the note. */
let checking = false;

/** @type {Array<() => void>} */
let settledWaiters = [];

/** Lets everyone waiting know the quantities are settled, unless another pass is on its way. */
function releaseSettled() {
  if (checking || queued) return;
  const waiters = settledWaiters;
  settledWaiters = [];
  for (const resolve of waiters) resolve();
}

/**
 * Resolves once no guard pass is checking or fixing quantities, so the cart on screen is the
 * cart that will be checked out. The note may still be saving.
 * @returns {Promise<void>}
 */
function quantitiesSettled() {
  if (!checking && !queued) return Promise.resolve();
  return new Promise((resolve) => settledWaiters.push(resolve));
}

/**
 * Brings nested lines back in step with their box and refreshes the note. Runs one pass at
 * a time; a change during a pass triggers one more pass afterwards.
 */
export function guardCart() {
  if (running) {
    queued = true;
    return running;
  }

  checking = true;
  running = (async () => {
    try {
      let cart = await fetchCart();

      // First match the nested lines to their box; if stock stops that, bring the box down instead.
      for (const findFixes of [quantityFixes, fallbackFixes]) {
        const fixes = findFixes(cart);
        if (Object.keys(fixes).length === 0) break;

        const result = await updateLines(fixes, { source: GUARD_SOURCE });
        cart = result.status ? await fetchCart() : result;
        if (Object.keys(quantityFixes(cart)).length === 0) break;
      }

      checking = false;
      releaseSettled();

      await syncCartNote(cart);
    } catch (error) {
      console.error(error);
    } finally {
      checking = false;
      running = null;
      if (queued) {
        queued = false;
        guardCart();
      } else {
        releaseSettled();
      }
    }
  })();

  return running;
}

/**
 * Cart changes in flight. While there are any, <html> carries data-cart-busy, which fades the
 * cart drawer and cart page under a spinner (snippets/cart-drawer.liquid), and every
 * [data-cart-status] live region says the cart is updating.
 */
let pendingChanges = 0;

/** A change that never settles stops counting after this long, so the cart can't stay faded. */
const BUSY_TIMEOUT = 10000;

/** @returns {boolean} Whether a cart change is in flight */
export function isCartBusy() {
  return pendingChanges > 0;
}

/** @param {number} change */
function countPending(change) {
  const wasBusy = isCartBusy();
  pendingChanges = Math.max(0, pendingChanges + change);

  const busy = isCartBusy();
  if (busy === wasBusy) return;

  document.documentElement.toggleAttribute('data-cart-busy', busy);
  for (const status of document.querySelectorAll('[data-cart-status]')) {
    if (status instanceof HTMLElement) {
      status.textContent = (busy ? status.dataset.textBusy : status.dataset.textDone) ?? '';
    }
  }
}

/**
 * Every cart change counts as busy until its request settles and the guard has brought any
 * nested lines back in step, so a half-updated box never shows. Changes made by the guard
 * itself don't start another guard pass.
 */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  countPending(1);

  let counted = true;
  const done = () => {
    if (!counted) return;
    counted = false;
    countPending(-1);
  };
  const timeout = setTimeout(done, BUSY_TIMEOUT);

  const applied = event.promise
    ? event.promise.then((result) => {
        if (result?.detail?.source !== GUARD_SOURCE) guardCart();
      })
    : Promise.resolve();

  applied
    .catch(() => {})
    .then(quantitiesSettled)
    .finally(() => {
      clearTimeout(timeout);
      done();
    });
});

if (document.querySelector('meta[name="uradora-cart-check"]')) guardCart();
